import { Fragment } from "react";
import { centsToPrice, IFetchState } from "itsf-ui-common";
import { useTranslation } from "react-i18next";
import { IUsageDetail } from "./interface/IUsageDetail";
import { formatUsage } from "./usageUtils";
import { RECORDS_PER_PAGE } from "@constants";
import { formatDateTime } from "@utils/dateFormat";
import {
    Box,
    Grid,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { HandleErrorAndLoading } from "@common/HandleErrorAndLoading/HandleErrorAndLoading";
import { Pagination } from "@common/Pagination/Pagination";

interface IParams {
    usageDetailList: IUsageDetail[];
    getUsageDetailListState: IFetchState;
    currentPage: number;
    totalPages: number;
    selectedPageSize: number;
    refreshUsageDetailList(): void;
    onPageSizeChange(selectedPage: number): void;
    setCurrentPage(selectedPage: number): void;
}

export const UsageDetailList = (props: IParams) => {
    const {
        usageDetailList,
        getUsageDetailListState,
        setCurrentPage,
        currentPage,
        onPageSizeChange,
        totalPages,
        selectedPageSize,
        refreshUsageDetailList,
    } = props;
    const { t } = useTranslation("usage");

    return (
        <div className="usage-detail">
            <HandleErrorAndLoading
                error={getUsageDetailListState.error}
                isLoading={getUsageDetailListState.isLoading}
                retryFetch={refreshUsageDetailList}
            >
                {usageDetailList.length > 0 && (
                    <Fragment>
                        <Box className="table-wrapper">
                            <Table className="table-main">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            <Typography color="primary">{t("date")}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography color="primary">{t("type")}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography color="primary">{t("value")}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography color="primary">{t("charge")}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography color="primary">{t("number")}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography color="primary">{t("location")}</Typography>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {usageDetailList.map((row) => (
                                        <TableRow aria-label="usage detail row" key={row.usageId}>
                                            <TableCell>
                                                <Typography>{formatDateTime(row.chargeStartDateTime)}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography>{row.usageCategory}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography>{formatUsage(row)}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography>
                                                    {t("price", { price: centsToPrice(row.amount) })}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography>{row.destinationNumber}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography>{row.destinationCountryCode}</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                        <Grid alignItems="center" container>
                            <Grid item md={4} xs={12}>
                                <div className="page-select">
                                    <Typography>{t("showPerPage")}</Typography>
                                    <Select
                                        aria-label="Page size select"
                                        className="page-size"
                                        disableUnderline
                                        displayEmpty
                                        fullWidth
                                        id="selected-page-size"
                                        value={selectedPageSize}
                                        variant="filled"
                                        onChange={(event) => onPageSizeChange(parseInt(event.target.value as string))}
                                    >
                                        {RECORDS_PER_PAGE.map((option) => (
                                            <MenuItem key={option} value={option}>
                                                {option}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </div>
                            </Grid>
                            <Grid className="pagination-wrapper" item md={8} xs={12}>
                                <Pagination
                                    currentPage={currentPage}
                                    setCurrentIndex={setCurrentPage}
                                    totalPages={totalPages}
                                />
                            </Grid>
                        </Grid>
                    </Fragment>
                )}
                {usageDetailList.length === 0 && <Typography className="no-result">{t("noResult")}</Typography>}
            </HandleErrorAndLoading>
        </div>
    );
};
